import { SetFilterAction } from "./actions";
import { isValuelessCondition } from "./conditionMeta";
import type { FilterStage } from "./state";

/**
 * Returns the sequence of {@link SetFilterAction}s needed to commit a set of
 * pending filter stages to the reducer, creating the column's filter from
 * scratch. Returns `null` when none of the stages are "meaningful" yet —
 * i.e. every stage has an empty value AND a non-valueless condition — so the
 * caller can keep the stages in local pending state and avoid creating a
 * no-op filter in the reducer.
 *
 * The actions are dispatched in stage-index order; the reducer appends them
 * one-by-one to the (initially empty) filter, so the final state contains all
 * stages in the expected order. This preserves any earlier pending stages
 * whose conditions the user picked but hasn't yet typed values into.
 */
export function buildPendingCommitActions(
  columnId: string,
  stages: FilterStage[],
): SetFilterAction[] | null {
  const hasMeaningful = stages.some(
    (s) => s.value !== "" || isValuelessCondition(s.condition),
  );
  if (!hasMeaningful) {
    return null;
  }
  return stages.map(
    (s, i) =>
      new SetFilterAction({
        columnId,
        stageIndex: i,
        value: s.value,
        condition: s.condition,
        ...(s.value2 !== undefined && { value2: s.value2 }),
      }),
  );
}
